import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import useIsMounted from "../../../../../../../hooks/use-is-mounted";
import useAccessLevels from "../../../../../../../hooks/use-access-levels";
import {Box, Card, Skeleton, Typography, useTheme} from "@mui/material";
import useIntersectionObserver from "../../../../../../../hooks/use-intersection-observer";
import useCombinedRefs from "../../../../../../../hooks/use-combined-refs";
import axios from "axios";
import DateUtils from "../../../../../../../../core/services/date-utils";
import {exportCSVFile} from "../../../../../../../../core/services/utils/utils";
import LineChart from "../../../../../../../components/base/chart/line-chart";
import Button from "../../../../../../../components/base/button/button";
import {ReactComponent as Excel} from "../../../../../../../../assets/images/export.svg";

const LostSalesAnalyticsDashboardViewChart = ({filters, fetchData, identifier, title, datasetEntries}) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.analytics.lost-sales-dashboard'});
    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();
    const canExport = operatorAccessLevels.packages.dashboard.lostSalesAnalytics
        && operatorAccessLevels.packages.dashboard.exportLostSalesAnalytics;

    const theme = useTheme();
    const chartColors = useMemo(() => Object.values(theme.palette['app-specific'].chart), [theme.palette])

    /**@type {React.MutableRefObject<HTMLElement>}*/
    const containerRef = useRef();
    const [intersectionObserverRef, isIntersecting] = useIntersectionObserver();
    const ref = useCombinedRefs(intersectionObserverRef, containerRef);

    /**
     * Fetches the data from the server based on the current query
     * @type {(function(*): void)}
     */
    const performSearch = useCallback(async (cancelTokenSource) => {
        const forApi = {
            period: {
                startDateTime: filters.startDate,
                endDateTime: filters.endDate,
            },
            productId: filters.part?.id,
            customerId: filters.customer?.id,
            operatorId: filters.operator?.id,
        }
        setLoading(true);
        const data = await fetchData(identifier, forApi, cancelTokenSource.token);
        if (!isMounted())
            return;
        if (!cancelTokenSource?.reason) {
            setLoading(false);
        }
        setData(data ?? []);
    }, [filters.startDate, filters.endDate, filters.customer?.id, filters.operator?.id, fetchData, identifier, isMounted, filters.part?.id])

    /**
     * Fetches the data from the server based on the current query after the component is mounted
     */
    useEffect(() => {
        if (!isIntersecting)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void performSearch(cancelTokenSource);
        return () => cancelTokenSource.cancel();
    }, [isIntersecting, performSearch])

    /**@type {Partial<import('chart.js').ChartData>}*/
    const [chartData, verticalTicksCount] = useMemo(() => {
        const dates = data.map(e => DateUtils.serverToLocal(e.date));
        dates.sort((a, b) => a.diff(b));
        const chartData = {
            datasets: [],
            labels: dates.map(e => e.format(DateUtils.customFormats.lostSalesAnalyticsChart)),
        };

        Object.entries(datasetEntries).forEach(([key, entry], index) => {
            chartData.datasets.push({
                data: data.reduce((acc, curr) => {
                    acc[DateUtils.serverToLocal(curr.date).format(DateUtils.customFormats.lostSalesAnalyticsChart)] = curr[key];
                    return acc;
                }, {}),
                label: entry.title,
                borderColor: chartColors[index % chartColors.length],
                pointBackgroundColor: chartColors[index % chartColors.length],
                pointBorderColor: chartColors[index % chartColors.length],
                borderDash: index === 0 ? undefined : [8, 8],
                borderWidth: 1.5,
                cubicInterpolationMode: 'monotone',
            });
        })
        var totalCount = Math.min(10, chartData.datasets.reduce((acc, curr) => Math.max(acc, new Set(Object.values(curr.data)).size), 0));
        return [
            chartData,
            totalCount,
        ];
    }, [chartColors, data, datasetEntries]);

    /**
     * Exports the chart underlying series as CSV.
     */
    const exportChartToCsv = useCallback(() => {
        const dateFormat = DateUtils.customFormats.lostSalesAnalyticsChart;
        const body = [...data]
            .sort((a, b) => DateUtils.serverToLocal(a.date).diff(DateUtils.serverToLocal(b.date)))
            .map((entry) => {
                const row = {
                    [t('dateColumn')]: DateUtils.serverToLocal(entry.date).format(dateFormat),
                };
                for (const [key, datasetEntry] of Object.entries(datasetEntries)) {
                    row[datasetEntry.title] = entry[key] ?? '';
                }
                return row;
            });
        exportCSVFile(body, title ?? t('export'));
    }, [data, datasetEntries, t, title]);

    return (
        <>
            <Box ref={ref}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1}}>
                    <Typography variant="body1" fontWeight={600}>
                        {title ?? ""}
                    </Typography>
                    {
                        canExport && !loading && !!data.length &&
                        <Button
                            variant={'outlined'}
                            color={'primary'}
                            size={'small'}
                            startIcon={<Excel/>}
                            onClick={exportChartToCsv}
                        >
                            {t('export')}
                        </Button>
                    }
                </Box>
                <Box sx={{width: '100%', marginBlock: 2}}>
                    {
                        loading &&
                        <Skeleton sx={{transform: 'none', padding: 2}} width={'100%'}>
                            <Card
                                variant={'outlined'}
                                sx={{height: window.innerHeight / 2}}
                            />
                        </Skeleton>

                    }
                    {
                        !loading &&
                        <Card variant={'outlined'} sx={{padding: 2}}>
                            <LineChart
                                height={window.innerHeight / 2}
                                data={chartData}
                                options={{
                                    scales: {
                                        y: {
                                            ticks: {
                                                maxTicksLimit: verticalTicksCount,
                                            },
                                        },
                                    },
                                }}
                            />
                        </Card>
                    }
                </Box>
            </Box>
        </>
    );
}

export default LostSalesAnalyticsDashboardViewChart;
